import React from 'react';
import { CheckCircle2, PlayCircle, FilePlus } from 'lucide-react';

const ExamCard = ({ 
  exam, 
  onExamClick,
  onReportClick,
  getReportStatusInfo,
  formatDate,
  formatDifficulty
}) => {
  const handleExamClick = () => {
    onExamClick(exam.student_info.student_id, exam.stamp_id, exam.exam_id);
  };

  const handleReportClick = (e) => {
    e.stopPropagation();
    onReportClick(exam);
  };

  // 담당 선생님 정보 가져오기
  const teacherName = exam.student_info?.teacher_name || exam.student_info?.in_charge_teacher;

  return (
    <div
      onClick={handleExamClick}
      className="cursor-pointer rounded-3xl border border-gray-200 bg-white p-[14.4px] min-w-[340px] shadow-lg transition-all duration-300 md:hover:shadow-2xl md:hover:border-primary/20"
    >
      {/* 테스트 정보 카드 스타일 (학습분석 디자인 적용) */}
      <div className="rounded-3xl bg-white">
        {/* 학생 정보 - 최상단 배치 */}
        <div className="mb-3 rounded-3xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2">
            <div 
              className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
              style={{ width: '32px', height: '32px' }}
              title={`학생 ID: ${exam.student_info.student_id}`}
            >
              {exam.student_info.student_id}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-medium">{exam.student_info.student_name}</h4>
              <p className="truncate text-xs text-gray-600">{exam.student_info.school_name} · {exam.student_info.school_grade}학년</p>
            </div>
            {teacherName && (
              <div className="text-xs text-gray-500">
                담당: <span className="font-medium text-gray-700">{teacherName}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* 테스트 정보 영역 */}
        <div className="rounded-3xl border border-gray-200 bg-white">
          <div className="px-4 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* 상태 아이콘 */}
                {exam.exam_status === 999 ? (
                  <CheckCircle2 className="text-green-600" size={32} />
                ) : exam.exam_status === 400 ? (
                  <PlayCircle className="text-orange-500" size={32} />
                ) : (
                  <FilePlus className="text-blue-500" size={32} />
                )}
                <div>
                  <h3 className="text-[14px] font-semibold">{exam.tree_name}</h3>
                  <p className="text-xs text-gray-500">{exam.edu_ver}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="rounded-3xl text-[9px] font-medium text-gray-500">
                  <div>생성: {exam.exam_created_at ? formatDate(exam.exam_created_at) : '-'}</div>
                  <div>수정: {exam.exam_updated_at ? formatDate(exam.exam_updated_at) : '-'}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="px-[14.4px] pb-[14.4px]">
            {/* 테스트 정보 (4열 그리드) */}
            <div className="grid grid-cols-4 gap-1 text-xs">
            <div className="rounded-3xl bg-gray-50 p-2 text-center">
              <div className="text-gray-500">TID</div>
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center gap-1">
                  <div className="text-sm font-semibold text-gray-800">{exam.exam_id}</div>
                  <span className="text-sm font-medium text-green-600">
                    {exam.exam_type === 'alpha' ? 'α' :
                     exam.exam_type === 'beta' ? 'β' :
                     exam.exam_type === 'gamma' ? 'γ' :
                     exam.exam_type === 'delta' ? 'δ' :
                     exam.exam_type === 'custom' ? '𝐂' :
                     exam.exam_type === 'epsilon' ? 'ε' :
                     exam.exam_type || '-'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">({exam.exam_round || 0}회)</div>
              </div>
            </div>
            <div className="rounded-3xl bg-gray-50 p-2 text-center">
              <div className="text-gray-500">난이도</div>
              <div className="flex flex-col items-center justify-center flex-1">
                <div className="text-sm font-semibold text-gray-800">
                  {exam.difficulty ? formatDifficulty(exam.difficulty) : '-'}
                </div>
              </div>
            </div>
            <div className="rounded-3xl bg-gray-50 p-2 text-center">
              <div className="text-gray-500">문제수</div>
              <div className="flex flex-col items-center justify-center flex-1">
                <div className="text-sm font-semibold text-gray-800">
                  {exam.length || ((exam.correct_count || 0) + (exam.wrong_count || 0))}
                </div>
              </div>
            </div>
            <div className="rounded-3xl bg-gray-50 p-2 text-center">
              <div className="text-gray-500">정답률</div>
              <div className="flex flex-col items-center">
                <div className="text-sm font-semibold text-green-600">
                  {(exam.length > 0 || ((exam.correct_count || 0) + (exam.wrong_count || 0)) > 0) ? 
                    `${Math.round(((exam.correct_count || 0) / (exam.length || ((exam.correct_count || 0) + (exam.wrong_count || 0)))) * 100)}%` : 
                    '-'
                  }
                </div>
                <div className="text-[8px] font-normal">
                  <span className="text-gray-400">(</span>
                  <span className="text-primary font-bold">{exam.correct_count || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-danger font-bold">{exam.wrong_count || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-black font-bold">{exam.length || ((exam.correct_count || 0) + (exam.wrong_count || 0))}</span>
                  <span className="text-gray-400">)</span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamCard; 